import { DatePipe } from '@angular/common';
import { Component, output, input, computed, inject, signal, OnInit, OnDestroy, effect } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Action, ActionDemander, ActionRecipient, ActionStatus } from '@shared/interfaces/actions';
import { MeetingAction } from '@shared/interfaces/meetings';
import { PointAction } from '@shared/interfaces/points';
import { UserInfo } from '@shared/models/user-info';
import { ActionsService } from '@shared/services/actions.service';
import { ActionScope } from '@shared/types/actions';
import { TogglerConfig } from '@shared/types/dev';
import { ScheduleUtil } from '@shared/utils';
import { finalize, forkJoin, map, Observable, of } from 'rxjs';
import { NotificationSuccess } from 'src/app/core/models';
import { Notifier } from 'src/app/core/services';
import { IContactService } from 'src/app/core/services/i-contact.service';
import { FormOptions } from 'src/app/common/models/form-options';

@Component({
  selector: 'idel-action-process',
  templateUrl: './action-process.component.html',
  styleUrl: './action-process.component.scss',
})
export class ActionProcessComponent implements OnInit, OnDestroy {
  private readonly datePipe = inject(DatePipe);
  private readonly iContactsService = inject(IContactService);
  private readonly service = inject(ActionsService);
  private readonly notifier = inject(Notifier);
  private readonly formOptions = inject(FormOptions);

  public readonly actionToProcess = input.required<PointAction | MeetingAction | Action>({ alias: 'action' });
  public readonly scope = input.required<ActionScope>();
  public readonly currentUser = input.required<UserInfo>();

  public readonly processed = output<void>();

  public readonly actionProcessFormGroup = new FormGroup({
    from: new FormControl<string>(''),
    to: new FormControl<string>(''),
    subject: new FormControl<string>(''),
    message: new FormControl<string>(''),
    deadline: new FormControl<Date | null>(null),
    answer: new FormControl<string | null>(''),
    validated: new FormControl<boolean>(false),
    status: new FormControl<string | null>(null, Validators.required),
  });

  public readonly togglerConfig = signal<TogglerConfig<'processed', 'not-processed'>>([
    {
      value: 'processed',
      label: 'Traité',
      click: () => this.actionProcessFormGroup.patchValue({ validated: true }),
    },
    {
      value: 'not-processed',
      label: 'Non traité',
      click: () => this.actionProcessFormGroup.patchValue({ validated: false }),
    },
  ]);

  public readonly deadlineSeverity = computed<string>(() => {
    const actionToProcess = this.actionToProcess();

    if (!actionToProcess || !actionToProcess.deadline) return 'error';

    if (this.isDeadlineAfterThisWeek()) return `info`;

    if (this.isAfterDeadline()) return `error`;

    if (this.isDeadlineThisWeek()) return `warn`;
  });

  public readonly deadlineDetail = computed<string>(() => {
    const actionToProcess = this.actionToProcess();

    if (!actionToProcess || !actionToProcess.deadline || !(actionToProcess.deadline instanceof Date)) return '';

    const parsedDate = this.datePipe.transform(actionToProcess.deadline, 'EEEE dd/MM/yyyy');
    const delaySinceToday = ScheduleUtil.delaySinceToday(actionToProcess.deadline);

    if (this.isDeadlineAfterThisWeek()) return `Doit être traitée pour le ${parsedDate} (${delaySinceToday})`;

    if (this.isAfterDeadline()) return `Devait être traitée pour le ${parsedDate} (${delaySinceToday})`;

    if (this.isDeadlineThisWeek()) return `Doit bientôt être traitée (Pour le : ${parsedDate}, ${delaySinceToday})`;
  });
  public readonly processingAction = signal<boolean>(false);
  public readonly actionsStatusesOptions = signal<ActionStatus[]>([]);

  public readonly actionEff = effect(() => {
    const actionToProcess = this.actionToProcess();

    if (actionToProcess) {
      this.populateForm();
    }
  });

  ngOnInit(): void {
    this.populateForm();
  }

  ngOnDestroy(): void {
    this.actionProcessFormGroup.reset({ validated: true });
    this.actionEff.destroy();
  }

  private resolveDemanderName(): Observable<string> {
    const { demander } = this.actionToProcess();

    if (demander.id === null) return of('Système');

    if (demander.kind === 'group') {
      const found = this.formOptions.groups().find((g) => g.id === demander.id);
      return of(found ? found.description : 'Inconnu');
    }

    if (demander.kind === 'service') {
      const found = this.formOptions.services().find((s) => s.id === demander.id);
      return of(found ? found.description : 'Inconnu');
    }

    return this.iContactsService.getUserWithDataAdmin({ UserIds: [demander.id] }).pipe(
      map((res) => {
        const contact = res[0]?.contactForUser;
        return contact?.nom ? `${contact.nom.toUpperCase()} ${contact.prenom}` : 'Inconnu';
      })
    );
  }

  private resolveRecipientName(): Observable<string> {
    const action = this.actionToProcess();
    const { recipient } = action;

    if (recipient.kind === 'group') {
      const found = this.formOptions.groups().find((g) => g.id === action.groupId);
      return of(found ? `Groupe - ${found.description}` : 'Inconnu');
    }

    if (recipient.kind === 'service') {
      const found = this.formOptions.services().find((s) => s.id === action.serviceId);
      return of(found ? `Service - ${found.description}` : 'Inconnu');
    }

    if (recipient.id === null) return of('Inconnu');

    return this.iContactsService
      .getContactAdmin(recipient.id)
      .pipe(map((contact) => (contact.nom ? `${contact.nom.toUpperCase()} ${contact.prenom}` : 'Inconnu')));
  }

  private populateForm(): void {
    forkJoin({
      actionStatusesOptions: this.service.fetchActionStatuses(),
      demanderName: this.resolveDemanderName(),
      recipientName: this.resolveRecipientName(),
    }).subscribe({
      next: ({ actionStatusesOptions, demanderName, recipientName }) => {
        this.actionsStatusesOptions.set(actionStatusesOptions);

        const foundStatus = this.actionsStatusesOptions().find(
          (status) => status.value === this.actionToProcess().status
        )?.value;

        this.actionProcessFormGroup.patchValue({
          from: demanderName,
          to: recipientName,
          subject: this.actionToProcess().subject ?? null,
          answer: this.actionToProcess().answer ?? null,
          message: this.actionToProcess().message ?? null,
          deadline: this.actionToProcess().deadline,
          validated: this.actionToProcess().done ?? false,
          status: foundStatus ?? null,
        });
      },
    });
  }

  public delayBeforeDeadline(): string {
    const actionToProcess = this.actionToProcess();

    if (!actionToProcess) return '';

    return ScheduleUtil.delaySinceToday(actionToProcess.deadline);
  }

  public isAfterDeadline(): boolean {
    const actionToProcess = this.actionToProcess();

    if (!actionToProcess) return false;

    return ScheduleUtil.isInThePast(actionToProcess.deadline);
  }

  private isDeadlineThisWeek(): boolean {
    const actionToProcess = this.actionToProcess();

    if (!actionToProcess) return false;

    return ScheduleUtil.isThisWeek(actionToProcess.deadline);
  }

  private isDeadlineAfterThisWeek(): boolean {
    const actionToProcess = this.actionToProcess();

    if (!actionToProcess) return false;

    return ScheduleUtil.isAfterThisWeek(actionToProcess.deadline);
  }

  public processAction(validation: { validate: boolean }): void {
    if (this.actionProcessFormGroup.invalid) return;

    if (this.processingAction()) return;

    const { value } = this.actionProcessFormGroup;

    this.processingAction.set(true);

    this.service
      .processAction({
        actionId: this.actionToProcess().id,
        scope: this.scope(),
        pointOrMeetingId:
          this.scope() === 'points'
            ? (this.actionToProcess() as PointAction).pointId
            : (this.actionToProcess() as MeetingAction).meetingId,
        query: {
          Statut: value.status!,
          TransmissionUserContactId: this.currentUser().contactId,
          Validated: validation.validate,
          Reponse: value.answer,
        },
      })
      .pipe(finalize(() => this.processingAction.set(false)))
      .subscribe(() => {
        this.notifier.showSuccess(new NotificationSuccess({ message: 'Action traitée' }));
        this.processed.emit();
        this.actionProcessFormGroup.reset();
      });
  }
}
